import { TableStorage } from '../data-table';

/**
 * Single localStorage bucket for the messaging tasks table (`MessageListRedesigned`).
 * Claim-side `MessageListPanel` does not persist — avoids a second key.
 */
export const MESSAGING_TABLE_STORAGE_KEY = 'message-list-redesigned';

export const MESSAGING_DEFAULT_PAGE_SIZE = 100;

/** One-time flag: upgrade old default pageSize 20 → 100, then never again. */
const PAGE_SIZE_MIGRATION_FLAG = 'enter.messaging.tablePageSizeMigratedV1';

/** Keys that must not remain (panel / ephemeral). */
const KEYS_TO_REMOVE = ['message-list-panel', 'message-list-panel-ephemeral'] as const;

const STORAGE_BUCKETS = ['defaults', 'filters', 'hidden_columns', 'hidden_filters'] as const;

type MessagingTableDefaults = {
    pageIndex?: number;
    pageSize?: number;
    sortBy?: unknown[];
};

function clearStorageBuckets(bucketName: string): void {
    for (const bucket of STORAGE_BUCKETS) {
        window.localStorage.removeItem(`table_${bucketName}_${bucket}`);
    }
}

function clearPanelMessagingTableStorage(): void {
    for (const key of KEYS_TO_REMOVE) {
        clearStorageBuckets(key);
    }
}

function looksLikeVirginDefaults(defaults: MessagingTableDefaults): boolean {
    return (
        (defaults.pageIndex || 0) === 0 && (!defaults.sortBy || defaults.sortBy.length === 0)
    );
}

/**
 * Resolve page size from `table_message-list-redesigned_defaults` only.
 * Removes panel localStorage keys. Seeds 100 when unset. After a one-time migration,
 * any explicitly saved size (including 20) is respected.
 */
export function ensureMessagingTablePageSizeDefaults(): number {
    clearPanelMessagingTableStorage();

    const current = TableStorage.getItem<MessagingTableDefaults>(MESSAGING_TABLE_STORAGE_KEY, 'defaults');
    const alreadyMigrated = window.localStorage.getItem(PAGE_SIZE_MIGRATION_FLAG) === '1';

    if (!alreadyMigrated) {
        window.localStorage.setItem(PAGE_SIZE_MIGRATION_FLAG, '1');
        // Old presenter default was 20 — upgrade virgin blobs once to 100.
        if (!current?.pageSize || (current.pageSize === 20 && looksLikeVirginDefaults(current))) {
            const seeded: MessagingTableDefaults = {
                pageIndex: 0,
                pageSize: MESSAGING_DEFAULT_PAGE_SIZE,
                sortBy: [],
            };
            TableStorage.saveItem(MESSAGING_TABLE_STORAGE_KEY, 'defaults', seeded);
            return MESSAGING_DEFAULT_PAGE_SIZE;
        }
    }

    if (current?.pageSize != null) {
        return current.pageSize;
    }

    const seeded: MessagingTableDefaults = {
        pageIndex: 0,
        pageSize: MESSAGING_DEFAULT_PAGE_SIZE,
        sortBy: [],
    };
    TableStorage.saveItem(MESSAGING_TABLE_STORAGE_KEY, 'defaults', seeded);
    return MESSAGING_DEFAULT_PAGE_SIZE;
}
